/**
 * Upload helper for editor-inserted images. Coordinates client-side
 * validation, dimension capture, presigned-URL request, and direct S3 PUT.
 * See Specs/editor-images-spec.md §7.2.
 */

import { requestArtifactImageUploadUrl, uploadFileToS3 } from './api'

const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const
const MAX_BYTES = 10 * 1024 * 1024 // 10MB — matches backend

const DEFAULT_WIDTH = 480
const DEFAULT_HEIGHT = 360

export interface UploadedEditorImage {
  src: string
  width: number
  height: number
  imageId: string
}

export class EditorImageUploadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'EditorImageUploadError'
  }
}

export async function uploadEditorImage(
  file: File,
  artifactId: string
): Promise<UploadedEditorImage> {
  if (!ALLOWED_MIME_TYPES.includes(file.type as typeof ALLOWED_MIME_TYPES[number])) {
    throw new EditorImageUploadError(
      `Unsupported image type. Use PNG, JPEG, GIF, or WebP.`
    )
  }
  if (file.size > MAX_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
    throw new EditorImageUploadError(
      `Image is ${sizeMB}MB; maximum is 10MB.`
    )
  }

  const dimensions = await readImageDimensions(file)

  const presigned = await requestArtifactImageUploadUrl(
    artifactId,
    file.name,
    file.type,
    file.size
  )

  await uploadFileToS3(presigned.uploadUrl, file)

  return {
    src: presigned.viewUrl,
    width: dimensions.width,
    height: dimensions.height,
    imageId: presigned.imageId,
  }
}

async function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file)
      const dims = { width: bitmap.width, height: bitmap.height }
      bitmap.close?.()
      return dims
    } catch {
      // Fall through to defaults — non-fatal.
    }
  }
  return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT }
}
